import apiClient from "./index";
import { Address, User, Page, Category } from "../types";
import { ApiError } from "../types/error";
import { isAxiosError } from "axios";
import { ApiResponse } from "./auth"; // Reusing ApiResponse from auth.ts
import { getItem } from "../utils/localStorage";
import { handleApiError } from "../utils/utils";

export interface OrderItemPayload {
  dishId: string;
  quantity: number;
}

export interface CreateOrderPayload {
  items: OrderItemPayload[];
  type: "DELIVERY" | "PICKUP";
  address?: Address;
}

export interface CreateOrderResponse {
  id: string;
}

export const createOrder = async (
  payload: CreateOrderPayload
): Promise<ApiResponse<CreateOrderResponse>> => {
  try {
    const dataToSend =
      payload.type === "DELIVERY"
        ? {
            ...payload,
            address: payload.address,
          }
        : {
            ...payload,
            address: null,
          };

    console.log("Creating order with payload !!! : ", dataToSend);

    const response = await apiClient.post<CreateOrderResponse>(
      "/orders",
      dataToSend
    );
    return { data: response.data, error: null };
  } catch (error) {
    return handleApiError(
      error,
      "An unknown error occurred during creating order."
    );
  }
};

export const updateOrder = async (
  payload: CreateOrderPayload,
  orderId: string
): Promise<ApiResponse<CreateOrderResponse>> => {
  try {
    const dataToSend =
      payload.type === "DELIVERY"
        ? {
            ...payload,
            address: payload.address,
          }
        : {
            ...payload,
            address: null,
          };

    console.log("updating order with payload !!! : ", dataToSend);

    const response = await apiClient.put<CreateOrderResponse>(
      "/orders/" + orderId,
      dataToSend
    );
    return { data: response.data, error: null };
  } catch (error) {
    return handleApiError(
      error,
      "An unknown error occurred during updating order."
    );
  }
};

export const deleteOrder = async (
  orderId: string
): Promise<ApiResponse<any>> => {
  try {
    const response = await apiClient.delete<any>("/orders/" + orderId);
    return { data: response.data, error: null };
  } catch (error) {
    return handleApiError(
      error,
      "An unknown error occurred during deleting order."
    );
  }
};

export interface PaymentIntentResponse {
  paymentIntentId: string;
  clientSecret: string;
  status: string;
}

export const createPaymentIntent = async (
  orderId: string
): Promise<ApiResponse<PaymentIntentResponse>> => {
  try {
    const token = getItem("authToken");
    if (!token) {
      return {
        data: null,
        error: {
          error: "UNAUTHORIZED",
          message: "No token found.",
          timestamp: new Date().toISOString(),
        },
      };
    }

    const response = await apiClient.post<PaymentIntentResponse>(
      `/orders/${orderId}/payment`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return { data: response.data, error: null };
  } catch (error) {
    if (isAxiosError(error) && error.response) {
      return { data: null, error: error.response.data as ApiError };
    }

    console.error("Error creating payment intent:", error);
    return {
      data: null,
      error: {
        error: "UNKNOWN",
        message: "An unknown error occurred while creating the payment intent.",
        timestamp: new Date().toISOString(),
      },
    };
  }
};

export interface OrderDish {
  id: string;
  name: string;
  description: string;
  price: number;
  imageUrl: string;
  categories: Category[];
  createdAt: string;
  updatedAt: string;
  programmed: boolean;
}

export interface OrderItem {
  dish: OrderDish;
  quantity: number;
}

export interface Order {
  id: string;
  customerResDto: User;
  status:
    | "CREATED"
    | "PENDING"
    | "VALIDATED"
    | "PREPARING"
    | "IN_DELIVERY"
    | "DELIVERED"
    | "DELIVERING"
    | "CANCELLED";
  itemsTotalPrice: number;
  deliveryFee: number;
  totalPrice: number;
  type: "DELIVERY" | "PICKUP";
  createdAt: string;
  updatedAt: string;
  items: OrderItem[];
  address: Address;
}

export const getUserOrders = async (
  page: number,
  size: number
): Promise<ApiResponse<Page<Order>>> => {
  try {
    const response = await apiClient.get<Page<Order>>("/orders/user", {
      params: {
        page,
        size,
      },
    });
    return { data: response.data, error: null };
  } catch (error) {
    return handleApiError(
      error,
      "An unknown error occurred during fetching user orders."
    );
  }
};

export const userCancelOrder = async (
  orderId: string
): Promise<ApiResponse<Order>> => {
  try {
    const token = getItem("authToken");
    if (!token) {
      return {
        data: null,
        error: {
          error: "UNAUTHORIZED",
          message: "No token found.",
          timestamp: new Date().toISOString(),
        },
      };
    }

    const response = await apiClient.post<Order>(
      `/orders/${orderId}/user-cancel`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return { data: response.data, error: null };
  } catch (error) {
    if (isAxiosError(error) && error.response) {
      return { data: null, error: error.response.data as ApiError };
    }
    return {
      data: null,
      error: {
        error: "UNKNOWN",
        message: "An unknown error occurred while cancelling the order.",
        timestamp: new Date().toISOString(),
      },
    };
  }
};
